/**
 * Drawer — right-side slide-over panel for in-context drill-downs.
 *
 * Sibling primitive to `Dialog` (#1184, Slice 1 of meta #1183). Same
 * portal + overlay mechanics; different geometry. Used today by the App
 * Integrations tab to drill into a variant's trigger rules / Slack
 * notification overrides without leaving the App configuration
 * context — the standalone variant Automation page that previously hosted
 * that surface is gone in the same slice.
 *
 * **Why not extend Dialog?** Dialog is centered-modal-with-overlay. A
 * `placement="right"` prop on Dialog would have leaked the geometric
 * variation across every existing consumer's mental model
 * ("…wait, is this thing a modal or a slide-over?"). Drawer reads as
 * its own affordance — the affordance the user sees ("a tray slid in
 * from the side, list on top of the page state I just clicked from")
 * is structurally distinct from a modal ("a centered card that
 * eclipses the page"). Same primitive ratios (portal, overlay,
 * close-on-overlay-click, close-on-ESC) implemented twice in 100 LoC
 * is cheaper than the cognitive cost of one parameterized primitive
 * that does both.
 *
 * **Accessibility.** `aria-modal="true"` on the panel, focus trap
 * inside it, focus restoration on close. The overlay catches outside
 * clicks. ESC closes (default; the host can opt out via
 * `closeOnEscape={false}` for forms with unsaved-changes guards —
 * none today, but the seam is there).
 *
 * **URL-state hosting is the caller's responsibility.** This primitive
 * is uncontrolled-ish: it takes `open` + `onOpenChange`, fires the
 * change handler when the user closes via ESC / overlay-click / close
 * button. The host wires those into `useSearchParams` so the drawer
 * state is bookmarkable. See `AppIntegrationsTab.tsx` for the canonical
 * binding.
 */
import * as React from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

// ── Context ─────────────────────────────────────────────────────────────

interface DrawerContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DrawerContext = React.createContext<DrawerContextValue | null>(null)

// ── Root ─────────────────────────────────────────────────────────────────

interface DrawerProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Close when the user presses ESC. Default true. A future form with
   * unsaved changes can pass `false` and run its own dirty-check before
   * calling the handler. */
  closeOnEscape?: boolean
  children: React.ReactNode
}

function Drawer({ open = false, onOpenChange, closeOnEscape = true, children }: DrawerProps) {
  const value = React.useMemo<DrawerContextValue>(
    () => ({ open, onOpenChange: onOpenChange ?? (() => {}) }),
    [open, onOpenChange],
  )

  // ESC handler at the root so it fires regardless of where focus
  // currently sits inside the drawer. Bound only while open so a
  // closed drawer doesn't intercept the page's ESC.
  React.useEffect(() => {
    if (!open || !closeOnEscape) return
    function handle(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onOpenChange?.(false)
      }
    }
    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [open, closeOnEscape, onOpenChange])

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
}

// ── Content ──────────────────────────────────────────────────────────────

interface DrawerContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  className?: string
  /** Accessible label for the panel. Should name the drawer's subject
   * (e.g. "Trigger rules for Staging"). Required for a11y; rendered
   * visually inside `DrawerHeader` when present, also wired as
   * `aria-label` on the panel for screen readers. */
  ariaLabel?: string
}

/** Focus-trap helper — keeps Tab / Shift+Tab inside the panel while open.
 * Implementation is the minimal "list focusable, wrap at edges" pattern;
 * we don't need a library for one drawer.
 *
 * NOTE: doesn't try to be clever about disabled controls / programmatic
 * focus changes inside the panel. The intent is the "ambient" trap — a
 * Tab from the last interactive control loops back to the first, and a
 * Shift+Tab from the first loops to the last. Anything fancier (roving
 * tabindex, sub-region traps) is deferred until a use case forces it.
 */
function useFocusTrap(open: boolean, ref: React.RefObject<HTMLDivElement | null>) {
  React.useEffect(() => {
    if (!open) return
    const panel = ref.current
    if (!panel) return

    // Save the focus target so we can restore it on close.
    const previouslyFocused = document.activeElement as HTMLElement | null

    // Defer the initial focus so the panel has time to render its
    // first focusable child before we hunt for it. Using rAF rather
    // than a 0-ms setTimeout because focus-on-mount in React 18
    // sometimes loses to React's own commit-phase work.
    const rafId = requestAnimationFrame(() => {
      const first = panel.querySelector<HTMLElement>(focusableSelector)
      first?.focus()
    })

    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return
      const focusables = Array.from(
        panel!.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((el) => !el.hasAttribute('disabled'))
      if (focusables.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusables[0]!
      const last = focusables[focusables.length - 1]!
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (active === first || !panel!.contains(active)) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('keydown', handleKey)
      // Restore focus to the element that opened the drawer if it's
      // still around — keeps the keyboard user oriented when the
      // drawer closes.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus()
      }
    }
  }, [open, ref])
}

const focusableSelector =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function DrawerContent({ children, className, ariaLabel, ...rest }: DrawerContentProps) {
  const ctx = React.useContext(DrawerContext)
  if (!ctx) throw new Error('DrawerContent must be used within Drawer')
  const panelRef = React.useRef<HTMLDivElement>(null)
  useFocusTrap(ctx.open, panelRef)

  if (!ctx.open) return null
  if (typeof document === 'undefined') return null

  // Portal to <body> so the panel escapes any ancestor stacking context
  // (transform / filter / backdrop-blur). Same reason Dialog portals —
  // see dialog.tsx's comment for the back-story.
  return createPortal(
    <div className="fixed inset-0 z-50" role="presentation">
      {/* Overlay — click to close */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-xs animate-in fade-in-0"
        onClick={() => ctx.onOpenChange(false)}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={cn(
          // Right-anchored slide-in. The width is responsive: 540px on
          // wide viewports (matches the mockup), full-width on narrow
          // ones so the rules list stays readable.
          'fixed right-0 top-0 bottom-0 z-50 flex flex-col bg-background border-l shadow-xl',
          'w-full sm:w-[540px] max-w-[90vw]',
          'animate-in slide-in-from-right duration-200',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
        {...rest}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

// ── Header / Body / Footer (mirrors Dialog's shape) ─────────────────────

interface DrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tiny label above the title, all-caps. Mirrors the mockup's
   * "Trigger rules" eyebrow. */
  eyebrow?: string
  /** The visible title. Distinct from the panel's `aria-label` so a
   * screen reader can hear "Trigger rules for Staging" while the eye
   * reads the two-line "Trigger rules" + "Staging" pattern. */
  title?: string
  /** Optional sub-line — the rule-fire description in the mockup. */
  subtitle?: React.ReactNode
}

function DrawerHeader({
  eyebrow,
  title,
  subtitle,
  className,
  children,
  ...props
}: DrawerHeaderProps) {
  const ctx = React.useContext(DrawerContext)
  if (!ctx) throw new Error('DrawerHeader must be used within Drawer')
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-5 py-4 border-b shrink-0',
        className,
      )}
      {...props}
    >
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
            {eyebrow}
          </p>
        )}
        {title && (
          <h2 className="text-lg font-semibold leading-tight">{title}</h2>
        )}
        {subtitle && (
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        )}
        {children}
      </div>
      <button
        type="button"
        onClick={() => ctx.onOpenChange(false)}
        className="rounded-sm p-1 text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-hidden focus:ring-2 focus:ring-ring"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

function DrawerBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex-1 overflow-y-auto px-5 py-4', className)} {...props} />
  )
}

function DrawerFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 px-5 py-3 border-t shrink-0',
        className,
      )}
      {...props}
    />
  )
}

export { Drawer, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter }
